const ADMIN_ORG_PROFILE_DRAFT_PREFIX = "cpAdminOrganizationProfileDraft";

export const ORG_PROFILE_FORM_DEFAULTS = Object.freeze({
  name: "",
  legalName: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  timezone: "America/Toronto",
  locale: "en-CA",
  currency: "CAD",
  businessNumber: "",
  gstHstRegistrationNumber: "",
  qstRegistrationNumber: "",
  additionalRegistrationLabel: "",
  additionalRegistrationNumber: "",
});

export function adminOrganizationProfileDraftKey(organizationId) {
  if (!organizationId) {
    return null;
  }
  return `${ADMIN_ORG_PROFILE_DRAFT_PREFIX}:${organizationId}`;
}

export function organizationProfileFromSettings(settings) {
  const profile = settings?.organization || {};
  return {
    name: profile.name || "",
    legalName: profile.legal_name || "",
    address: profile.address || "",
    phone: profile.phone || "",
    email: profile.email || "",
    website: profile.website || "",
    timezone: profile.timezone || ORG_PROFILE_FORM_DEFAULTS.timezone,
    locale: profile.locale || ORG_PROFILE_FORM_DEFAULTS.locale,
    currency: profile.currency || ORG_PROFILE_FORM_DEFAULTS.currency,
    businessNumber: profile.business_number || "",
    gstHstRegistrationNumber: profile.gst_hst_registration_number || "",
    qstRegistrationNumber: profile.qst_registration_number || "",
    additionalRegistrationLabel: profile.additional_registration_label || "",
    additionalRegistrationNumber: profile.additional_registration_number || "",
  };
}

export function organizationProfileFormToPatch(form) {
  return {
    name: form.name.trim(),
    legal_name: form.legalName.trim(),
    address: form.address.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    website: form.website.trim(),
    timezone: form.timezone,
    locale: form.locale,
    currency: form.currency,
    business_number: String(form.businessNumber || "").trim(),
    gst_hst_registration_number: String(
      form.gstHstRegistrationNumber || "",
    ).trim(),
    qst_registration_number: String(form.qstRegistrationNumber || "").trim(),
    additional_registration_label: String(
      form.additionalRegistrationLabel || "",
    ).trim(),
    additional_registration_number: String(
      form.additionalRegistrationNumber || "",
    ).trim(),
  };
}

export function organizationProfilesEqual(left, right) {
  return Object.keys(ORG_PROFILE_FORM_DEFAULTS).every(
    (field) => (left?.[field] ?? "") === (right?.[field] ?? ""),
  );
}

function readRawOrganizationProfileDraft(organizationId) {
  const key = adminOrganizationProfileDraftKey(organizationId);
  if (!key || typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Returns a user-edited draft only. Ignores legacy auto-saved drafts that lack
 * the userEdited marker so they cannot interfere with DB hydration.
 */
export function readUserOrganizationProfileDraft(organizationId) {
  const parsed = readRawOrganizationProfileDraft(organizationId);
  if (!parsed) {
    return null;
  }

  if (parsed.userEdited === true && parsed.form && typeof parsed.form === "object") {
    return {
      ...ORG_PROFILE_FORM_DEFAULTS,
      ...parsed.form,
    };
  }

  return null;
}

/** @deprecated Use readUserOrganizationProfileDraft */
export function readAdminOrganizationProfileDraft(organizationId) {
  return readUserOrganizationProfileDraft(organizationId);
}

/**
 * Resolves the form state for hydration: DB settings first, user draft only
 * when explicitly marked as user-edited and different from saved values.
 */
export function resolveOrganizationProfileHydration(settings, organizationId) {
  const savedProfile = organizationProfileFromSettings(settings);
  const userDraft = readUserOrganizationProfileDraft(organizationId);

  if (userDraft && !organizationProfilesEqual(userDraft, savedProfile)) {
    return {
      savedProfile,
      formProfile: userDraft,
      restoreUserDraft: true,
    };
  }

  return {
    savedProfile,
    formProfile: savedProfile,
    restoreUserDraft: false,
  };
}

export function writeUserOrganizationProfileDraft(organizationId, form) {
  const key = adminOrganizationProfileDraftKey(organizationId);
  if (!key || typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({
        userEdited: true,
        form,
      }),
    );
  } catch (error) {
    console.warn("[AdminOrganization] failed to persist profile draft", error);
  }
}

/** @deprecated Use writeUserOrganizationProfileDraft */
export function writeAdminOrganizationProfileDraft(organizationId, form) {
  writeUserOrganizationProfileDraft(organizationId, form);
}

export function clearAdminOrganizationProfileDraft(organizationId) {
  const key = adminOrganizationProfileDraftKey(organizationId);
  if (!key || typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    console.warn("[AdminOrganization] failed to clear profile draft", error);
  }
}

/** @deprecated Use readUserOrganizationProfileDraft + organizationProfilesEqual */
export function shouldApplyProfileDraft(draft, savedProfile) {
  if (!draft || organizationProfilesEqual(draft, savedProfile)) {
    return false;
  }

  const draftDroppedSavedValue = Object.keys(ORG_PROFILE_FORM_DEFAULTS).some(
    (field) => {
      const savedValue = (savedProfile[field] ?? "").trim();
      const draftValue = (draft[field] ?? "").trim();
      return savedValue && !draftValue;
    },
  );

  return !draftDroppedSavedValue;
}
